import React, { useState } from "react";

import { isEmpty, map, upperFirst } from "lodash/fp";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Typography } from "@mui/material";

import SettingAlert from "@app/analytics/account/components/SettingAlert";
import BasicModal, { ModalProps } from "@app/analytics/components/BasicModal";
import TextField from "@app/analytics/components/TextField";
import { EnterpriseTeam } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { yupResolver } from "@app/utils/forms/yupResolver";
import { getStringType } from "@app/utils/utils";

const FORM_ID = "add-team";
const MIN_TEAM_NAME_CHARACTERS = 2;
const MAX_TEAM_NAME_CHARACTERS = 60;

const teamNameValidationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Required")
    .min(MIN_TEAM_NAME_CHARACTERS)
    .max(MAX_TEAM_NAME_CHARACTERS),
});

interface NewTeamModalProps extends ModalProps {
  onNext: (team: EnterpriseTeam) => void;
  visible: boolean;
}

const NewTeamModal = ({
  onClose,
  onNext,
  open,
  visible,
}: NewTeamModalProps) => {
  const {
    createTeam,
    workspace: { teams },
  } = useWorkspace();
  const [loading, setLoading] = useState<boolean>(false);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);

  const {
    clearErrors,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({ resolver: yupResolver(teamNameValidationSchema) });

  const textField = register("name");
  const hasError = !isEmpty(errors.name?.message);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (hasError) {
      clearErrors();
    }

    return textField.onChange(event);
  };

  const clearState = () => {
    reset();
    setLoading(false);
  };

  const handleCreateTeamError = () => {
    setLoading(false);
    setShowErrorAlert(true);
  };

  const handleCreateTeamSuccess = (team: EnterpriseTeam) => {
    onNext(team);
    clearState();
  };

  const validateUniqueTeamName = (name: string) => {
    if (
      name.length < MIN_TEAM_NAME_CHARACTERS ||
      name.length > MAX_TEAM_NAME_CHARACTERS
    ) {
      return false;
    }

    const existingTeamNames = map("name", teams);
    if (existingTeamNames.includes(name)) {
      setError(textField.name, {
        type: "custom",
        message: "This team already exists in this Workspace",
      });
      return false;
    }

    return true;
  };

  const handleCreateTeam = () => {
    const teamName = getValues("name");

    const isValidTeamName = validateUniqueTeamName(teamName);
    if (isValidTeamName) {
      setLoading(true);

      return createTeam(teamName, {
        onSuccess: handleCreateTeamSuccess,
        onError: handleCreateTeamError,
      });
    }
  };

  const handleEscPress = () => {
    const value = getValues("name");
    if (value) {
      reset();
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    onClose();
    clearState();
  };

  return (
    <BasicModal
      formId={FORM_ID}
      loading={loading}
      modalSx={{ visibility: visible ? "visible" : "hidden" }}
      onClose={handleClose}
      onSubmit={handleCreateTeam}
      open={open}
      submitText="Create Team"
      title="New Team"
      sx={{ width: "500px" }}
    >
      <Typography
        variant="bodyPrimaryReg"
        sx={{ mb: 3, color: "shades.neutral.500" }}
      >
        Teams allow your organization to customize settings for different groups
        of people
      </Typography>
      <Typography
        variant="bodyPrimaryMed"
        sx={{ mb: 1.5, color: "shades.neutral.700" }}
      >
        Name your team
      </Typography>
      <SettingAlert
        severity="error"
        show={showErrorAlert}
        onHide={() => setShowErrorAlert(false)}
      >
        Something went wrong when creating the team. Please try again or refresh
        the page.
      </SettingAlert>
      <form id={FORM_ID} onSubmit={handleSubmit(handleCreateTeam)}>
        <TextField
          InputProps={{
            ...textField,
            placeholder: 'Something like "Marketing"',
          }}
          error={hasError}
          helperText={upperFirst(getStringType(errors.name?.message) ?? "")}
          onChange={handleChange}
          onEscPress={handleEscPress}
          sx={{
            "& .MuiOutlinedInput-root input": {
              fontSize: "1rem",
            },
          }}
        />
      </form>
    </BasicModal>
  );
};

export default NewTeamModal;
